import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useConsent } from "@/components/consent/ConsentProvider";

interface CookieRow {
  name: string;
  issuer: string;
  purpose: string;
  category: "essential" | "analytics" | "ads" | "personalization";
  duration: string;
}

const COOKIES: CookieRow[] = [
  {
    name: "sb-mxvzxbolwuuqyresuecy-auth-token",
    issuer: "Delapresse (Supabase Auth)",
    purpose: "Maintain the authenticated session.",
    category: "essential",
    duration: "1 hour (refresh-token rotates)",
  },
  {
    name: "dp_consent_v1",
    issuer: "Delapresse",
    purpose: "Remember your cookie preferences.",
    category: "essential",
    duration: "12 months",
  },
  {
    name: "sidebar:state",
    issuer: "Delapresse",
    purpose: "Remember the collapsed/expanded sidebar state.",
    category: "essential",
    duration: "Session",
  },
  {
    name: "i18nextLng / ui_language",
    issuer: "Delapresse",
    purpose: "Remember the selected interface language.",
    category: "essential",
    duration: "12 months",
  },
  {
    name: "_ga, _ga_<container>",
    issuer: "Google Analytics",
    purpose: "Aggregate audience analytics (page views, sessions).",
    category: "analytics",
    duration: "Up to 2 years",
  },
  {
    name: "_gcl_au",
    issuer: "Google Ads",
    purpose: "Conversion attribution.",
    category: "ads",
    duration: "90 days",
  },
];

function CookieCategoryBadge({ category }: { category: CookieRow["category"] }) {
  const { t } = useTranslation();
  const className: Record<CookieRow["category"], string> = {
    essential: "bg-secondary text-foreground",
    analytics: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
    ads: "bg-orange-500/10 text-orange-700 dark:text-orange-300",
    personalization: "bg-purple-500/10 text-purple-700 dark:text-purple-300",
  };
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${className[category]}`}>
      {t(`legal.cookies.page.categories.${category}`)}
    </span>
  );
}

export default function CookiesPage() {
  const { t } = useTranslation();
  const { open } = useConsent();

  useEffect(() => {
    document.title = `${t("legal.cookies.page.title")} — Delapresse`;
    return () => {
      document.title = "Delapresse";
    };
  }, [t]);

  return (
    <div className="min-h-screen bg-background">
      <article className="mx-auto max-w-4xl px-6 py-16">
        <header className="mb-10">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            {t("legal.common.label")}
          </p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl">
            {t("legal.cookies.page.title")}
          </h1>
          <p className="mt-4 text-base leading-7 text-foreground/90">{t("legal.cookies.page.intro")}</p>
          <div className="mt-6">
            <Button onClick={open}>{t("legal.cookies.page.manageButton")}</Button>
          </div>
        </header>

        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">{t("legal.cookies.page.tableHeaders.name")}</th>
                <th className="px-3 py-2">{t("legal.cookies.page.tableHeaders.issuer")}</th>
                <th className="px-3 py-2">{t("legal.cookies.page.tableHeaders.purpose")}</th>
                <th className="px-3 py-2">{t("legal.cookies.page.tableHeaders.category")}</th>
                <th className="px-3 py-2">{t("legal.cookies.page.tableHeaders.duration")}</th>
              </tr>
            </thead>
            <tbody>
              {COOKIES.map((row) => (
                <tr key={row.name} className="border-t border-border align-top">
                  <td className="px-3 py-2 font-mono text-xs text-foreground">{row.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{row.issuer}</td>
                  <td className="px-3 py-2 text-muted-foreground">{row.purpose}</td>
                  <td className="px-3 py-2">
                    <CookieCategoryBadge category={row.category} />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{row.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-8 text-sm leading-6 text-muted-foreground">{t("legal.cookies.page.disclaimer")}</p>
      </article>
    </div>
  );
}
